// Rechnungsprüfung: Abgleich je Rechnungszeile zwischen bestellt, geliefert und berechnet (Menge UND Preis) – reine
// Funktionen für Server und Oberfläche. Mengen in Tausendstel der Basiseinheit, Geld in Cent netto.
//
//   Menge  berechnet (dazu, was frühere Rechnungen schon berechnet haben) gegen geliefert
//   Preis  Zeilenbetrag der Rechnung gegen dieselbe Menge zum bestellten Preis
// Innerhalb der Toleranzen gilt eine Zeile als stimmig. Was darüber liegt, ist eine Abweichung: zu unseren Lasten
// sperrt sie die Freigabe, bis jemand sie mit Grund annimmt oder eine Gutschrift anfordert; zu unseren Gunsten ist
// sie nur ein Hinweis.
import { formatEuro, formatQty } from '@shared/format'

export interface MatchTolerances {
  /** erlaubte Mehrmenge der Rechnung gegenüber der Lieferung in Basispunkten */
  qtyToleranceBp: number
  /** erlaubte Preisabweichung je Zeile in Basispunkten des erwarteten Betrags … */
  priceToleranceBp: number
  /** … und als fester Betrag je Zeile (es gilt der größere Wert) */
  priceToleranceCents: number
}

export const DEFAULT_TOLERANCES: MatchTolerances = { qtyToleranceBp: 0, priceToleranceBp: 50, priceToleranceCents: 100 }

export const DIFFERENCE_TYPES = ['no_reference', 'not_received', 'qty_over', 'qty_under', 'price_over', 'price_under', 'damaged'] as const
export type DifferenceType = (typeof DIFFERENCE_TYPES)[number]

export const DIFFERENCE_LABELS: Record<DifferenceType | 'deposit', string> = {
  no_reference: 'Ohne Bestellbezug',
  not_received: 'Noch nicht geliefert',
  qty_over: 'Mehr berechnet als geliefert',
  qty_under: 'Weniger berechnet als geliefert',
  price_over: 'Preis über der Bestellung',
  price_under: 'Preis unter der Bestellung',
  damaged: 'Beschädigt geliefert',
  deposit: 'Pfand weicht ab',
}

export interface MatchDifference {
  type: DifferenceType
  /** sperrt die Freigabe, bis entschieden ist */
  blocking: boolean
  /** Mengenabweichung in Basiseinheit (berechnet − geliefert), 0 bei Preisabweichungen */
  qtyDelta: number
  /** Geld, um das es geht, in Cent netto – positiv = zu unseren Lasten */
  amountCents: number
  /** fertiger deutscher Satz */
  message: string
}

/** Preis als exaktes Paar: `cents` je `perQty` Tausendstel eines Gebindes mit `unitBaseQty` Basiseinheiten. */
export interface PricePair {
  unitPriceCents: number
  pricePerQty: number
  unitBaseQty: number
  discountBp: number
}

export interface MatchLineInput {
  description: string
  /** Kurzform und Basismenge des Gebindes der Rechnungszeile – in ihm stehen die Sätze */
  unit: string | null
  unitBaseQty: number
  invoicedBaseQty: number
  /** Zeilenbetrag netto nach Zeilenrabatt */
  invoicedNetCents: number
  /** worauf sich die Zeile bezieht; null = ohne Bezug */
  reference: 'receipt' | 'order' | null
  /** bestellte Menge (Basis); null, wenn die Lieferung ohne Bestellung kam */
  orderedBaseQty: number | null
  /** gelieferte Menge des Bezugs (Basis) */
  receivedBaseQty: number
  /** davon beschädigt angekommen (Basis) */
  damagedBaseQty: number
  /** aus anderen, bereits freigegebenen Rechnungen zum selben Bezug (Basis) */
  invoicedBeforeBaseQty: number
  /** erwarteter Preis: aus der Bestellung, sonst aus dem Wareneingang */
  expected: PricePair | null
}

export interface MatchLineResult {
  /** ok = stimmig · info = Abweichung zu unseren Gunsten · difference = muss entschieden werden */
  status: 'ok' | 'info' | 'difference'
  differences: MatchDifference[]
  /** Betrag derselben Menge zum erwarteten Preis; null ohne Bezug */
  expectedNetCents: number | null
  /** Rechnung − erwartet (Preisanteil), 0 ohne Bezug */
  priceDeltaCents: number
  /** berechnet + früher berechnet − geliefert (Basis) */
  qtyDelta: number
  /** Summe der sperrenden Abweichungen in Cent netto */
  blockingCents: number
  /** Fingerabdruck der sperrenden Abweichungen – eine Entscheidung gilt nur für genau diesen Stand */
  signature: string
}

/** Betrag einer Basismenge zu einem Preis-Paar, kaufmännisch gerundet (ein Schritt, eine Rundung). */
export function amountAt(baseQty: number, price: PricePair): number {
  const numerator = BigInt(baseQty) * BigInt(price.unitPriceCents) * 1000n * BigInt(10_000 - price.discountBp)
  const denominator = BigInt(price.pricePerQty) * BigInt(price.unitBaseQty) * 10_000n
  if (denominator <= 0n) throw new RangeError('Preiseinheit und Gebinde müssen größer als null sein.')
  const sign = numerator < 0n ? -1n : 1n
  const rounded = (sign * numerator * 2n + denominator) / (denominator * 2n)
  return Number(sign * rounded)
}

const inUnit = (baseQty: number, input: Pick<MatchLineInput, 'unit' | 'unitBaseQty'>): string =>
  formatQty(Math.round((Math.abs(baseQty) * 1000) / input.unitBaseQty), input.unit ? { unit: input.unit } : {})

export function matchLine(input: MatchLineInput, tolerances: MatchTolerances): MatchLineResult {
  const differences: MatchDifference[] = []
  if (input.reference === null) {
    differences.push({
      type: 'no_reference',
      blocking: true,
      qtyDelta: input.invoicedBaseQty,
      amountCents: input.invoicedNetCents,
      message: `${input.description}: Zu dieser Zeile gibt es weder Bestellung noch Wareneingang.`,
    })
    return finish(differences, null, 0, input.invoicedBaseQty)
  }
  const expectedNetCents = input.expected ? amountAt(input.invoicedBaseQty, input.expected) : null
  const billed = input.invoicedBaseQty + input.invoicedBeforeBaseQty
  const qtyDelta = billed - input.receivedBaseQty
  const qtyTolerance = Math.floor((input.receivedBaseQty * tolerances.qtyToleranceBp) / 10_000)
  const perBase = (baseQty: number): number => (input.expected ? amountAt(baseQty, input.expected) : input.invoicedBaseQty > 0 ? Math.round((input.invoicedNetCents * baseQty) / input.invoicedBaseQty) : 0)

  if (input.receivedBaseQty === 0 && billed > 0) {
    differences.push({
      type: 'not_received',
      blocking: true,
      qtyDelta,
      amountCents: input.invoicedNetCents,
      message: `${input.description}: berechnet ${inUnit(input.invoicedBaseQty, input)}, geliefert ist noch nichts.`,
    })
  } else if (qtyDelta > qtyTolerance) {
    const earlier = input.invoicedBeforeBaseQty > 0 ? ` (dazu ${inUnit(input.invoicedBeforeBaseQty, input)} aus früheren Rechnungen)` : ''
    differences.push({
      type: 'qty_over',
      blocking: true,
      qtyDelta,
      amountCents: perBase(Math.min(qtyDelta, input.invoicedBaseQty)),
      message: `${input.description}: berechnet ${inUnit(input.invoicedBaseQty, input)}${earlier}, geliefert ${inUnit(input.receivedBaseQty, input)} – ${inUnit(qtyDelta, input)} zu viel.`,
    })
  } else if (qtyDelta < 0) {
    differences.push({
      type: 'qty_under',
      blocking: false,
      qtyDelta,
      amountCents: -perBase(-qtyDelta),
      message: `${input.description}: geliefert ${inUnit(input.receivedBaseQty, input)}, berechnet erst ${inUnit(billed, input)} – ${inUnit(-qtyDelta, input)} stehen noch aus.`,
    })
  }

  const priceDeltaCents = expectedNetCents === null ? 0 : input.invoicedNetCents - expectedNetCents
  if (expectedNetCents !== null) {
    const allowed = Math.max(tolerances.priceToleranceCents, Math.floor((Math.abs(expectedNetCents) * tolerances.priceToleranceBp) / 10_000))
    if (Math.abs(priceDeltaCents) > allowed) {
      const over = priceDeltaCents > 0
      differences.push({
        type: over ? 'price_over' : 'price_under',
        blocking: over,
        qtyDelta: 0,
        amountCents: priceDeltaCents,
        message: `${input.description}: berechnet ${formatEuro(input.invoicedNetCents)}, ${input.reference === 'order' || input.orderedBaseQty !== null ? 'laut Bestellung' : 'laut Wareneingang'} ${formatEuro(expectedNetCents)} – ${formatEuro(Math.abs(priceDeltaCents))} ${over ? 'mehr' : 'weniger'}.`,
      })
    }
  }

  const damagedBilled = Math.min(input.damagedBaseQty, input.invoicedBaseQty)
  if (damagedBilled > 0) {
    differences.push({
      type: 'damaged',
      blocking: true,
      qtyDelta: 0,
      amountCents: input.invoicedBaseQty > 0 ? Math.round((input.invoicedNetCents * damagedBilled) / input.invoicedBaseQty) : 0,
      message: `${input.description}: ${inUnit(damagedBilled, input)} beschädigt angekommen, aber voll berechnet.`,
    })
  }
  return finish(differences, expectedNetCents, priceDeltaCents, qtyDelta)
}

function finish(differences: MatchDifference[], expectedNetCents: number | null, priceDeltaCents: number, qtyDelta: number): MatchLineResult {
  const blocking = differences.filter((entry) => entry.blocking)
  return {
    status: blocking.length > 0 ? 'difference' : differences.length > 0 ? 'info' : 'ok',
    differences,
    expectedNetCents,
    priceDeltaCents,
    qtyDelta,
    blockingCents: blocking.reduce((sum, entry) => sum + Math.max(0, entry.amountCents), 0),
    signature: differenceSignature(blocking),
  }
}

export const differenceSignature = (differences: readonly Pick<MatchDifference, 'type' | 'qtyDelta' | 'amountCents'>[]): string =>
  differences.map((entry) => `${entry.type}:${entry.qtyDelta}:${entry.amountCents}`).sort().join('|')

// ---- Entscheidung an der Zeile (steht in customFields der Rechnungszeile bzw. des Kopfs für das Pfand) -------------

export const MATCH_DECISIONS = ['accept', 'credit'] as const
export type MatchDecisionKind = (typeof MATCH_DECISIONS)[number]

export interface MatchDecision {
  decision: MatchDecisionKind
  reason: string
  /** wer entschieden hat und wann (ISO-Zeitpunkt) */
  byName: string
  at: string
  /** Stand der Abweichungen, für den die Entscheidung gilt */
  signature: string
  /** Betrag der Abweichung, über die entschieden wurde, in Cent netto */
  amountCents: number
  /** Betrag der Gutschriftsanforderung in Cent netto (nur bei `credit`) */
  creditCents: number
}

export const MATCH_FIELD = 'matchDecision'

/** Liest eine gespeicherte Entscheidung; passt sie nicht mehr zum Stand der Abweichungen, gilt sie nicht. */
export function readDecision(customFields: Readonly<Record<string, unknown>>, signature: string): MatchDecision | null {
  const raw = customFields[MATCH_FIELD]
  if (!raw || typeof raw !== 'object') return null
  const value = raw as Partial<MatchDecision>
  if ((value.decision !== 'accept' && value.decision !== 'credit') || typeof value.reason !== 'string' || value.signature !== signature) return null
  return {
    decision: value.decision,
    reason: value.reason,
    byName: typeof value.byName === 'string' ? value.byName : '',
    at: typeof value.at === 'string' ? value.at : '',
    signature,
    amountCents: typeof value.amountCents === 'number' && Number.isSafeInteger(value.amountCents) ? value.amountCents : 0,
    creditCents: typeof value.creditCents === 'number' && Number.isSafeInteger(value.creditCents) ? value.creditCents : 0,
  }
}

/** Preisvorschlag für die Stammdaten aus einer Rechnungszeile: Cent netto je Bestelleinheit (Preiseinheit 1000). */
export function priceForOrderUnit(invoicedNetCents: number, invoicedBaseQty: number, orderUnitBaseQty: number): number | null {
  if (invoicedBaseQty <= 0 || orderUnitBaseQty <= 0) return null
  return Math.round((invoicedNetCents * orderUnitBaseQty) / invoicedBaseQty)
}

// ---- Zusatzzeilen: Zuschlag, Abzug, Pfand ohne Warenzeile, Leergut-Gutschrift (AP8a) -------------------------------

export interface ExtraLineMatchInput {
  kind: 'fee' | 'discount' | 'deposit' | 'deposit_return'
  description: string
  /** Betrag netto mit Vorzeichen, wie im Beleg (Abzug und Leergut negativ) */
  netCents: number
  /** Bezug auf eine Zeile von Bestellung oder Wareneingang – null: steht nur auf der Rechnung */
  reference: 'receipt' | 'order' | null
  /** Betrag derselben Menge im Vorgänger, mit Vorzeichen (nur Zuschlag und Abzug mit Bezug) */
  expectedNetCents: number | null
  /** Pfand und Leergut: Betrag laut Rechnung (mit Vorzeichen), wenn er angegeben wurde */
  statedNetCents: number | null
}

export interface ExtraLineMatchResult {
  status: 'ok' | 'info' | 'difference'
  differences: MatchDifference[]
  blockingCents: number
  signature: string
  /** ein Satz zur Zeile – die erste Abweichung oder der Hinweis */
  message: string
}

/**
 * Abgleich einer Zusatzzeile. Zu unseren Lasten (sperrt, bis entschieden): ein Zuschlag ohne Bezug, ein Zuschlag über
 * dem der Bestellung, ein Abzug unter dem der Bestellung, ein Pfand- oder Leergutbetrag der Rechnung über dem der
 * Pfandart. Zu unseren Gunsten ist es nur ein Hinweis; eine Leergut-Gutschrift ohne Bezug ist immer einer (das
 * Leergutkonto bucht die Rechnung nicht).
 */
export function matchExtraLine(input: ExtraLineMatchInput, tolerances: MatchTolerances): ExtraLineMatchResult {
  const differences: MatchDifference[] = []
  let hint: string | null = null
  const allowedFor = (expected: number): number => Math.max(tolerances.priceToleranceCents, Math.floor((Math.abs(expected) * tolerances.priceToleranceBp) / 10_000))
  if (input.kind === 'fee' || input.kind === 'discount') {
    const what = input.kind === 'fee' ? 'Zuschlag' : 'Abzug'
    if (input.reference === null || input.expectedNetCents === null) {
      if (input.kind === 'fee' && input.netCents > 0) {
        differences.push({
          type: 'no_reference',
          blocking: true,
          qtyDelta: 0,
          amountCents: input.netCents,
          message: `${input.description}: Zuschlag von ${formatEuro(input.netCents)}, der weder in der Bestellung noch im Wareneingang steht.`,
        })
      } else if (input.kind === 'discount') hint = `${input.description}: Abzug des Lieferanten über ${formatEuro(Math.abs(input.netCents))}.`
    } else {
      const delta = input.netCents - input.expectedNetCents
      if (Math.abs(delta) > allowedFor(input.expectedNetCents)) {
        const over = delta > 0
        differences.push({
          type: over ? 'price_over' : 'price_under',
          blocking: over,
          qtyDelta: 0,
          amountCents: delta,
          message: `${input.description}: ${what} ${formatEuro(Math.abs(input.netCents))}, laut ${input.reference === 'order' ? 'Bestellung' : 'Wareneingang'} ${formatEuro(Math.abs(input.expectedNetCents))} – ${formatEuro(Math.abs(delta))} ${over ? 'zu unseren Lasten' : 'zu unseren Gunsten'}.`,
        })
      }
    }
  } else {
    if (input.statedNetCents !== null) {
      // der Kern bewertet Pfand mit der Pfandart – weicht der Betrag der Rechnung ab, steht es hier
      const delta = input.statedNetCents - input.netCents
      if (Math.abs(delta) > allowedFor(input.netCents)) {
        const over = delta > 0
        differences.push({
          type: over ? 'price_over' : 'price_under',
          blocking: over,
          qtyDelta: 0,
          amountCents: delta,
          message: `${input.description}: laut Rechnung ${formatEuro(input.statedNetCents)}, laut Pfandart ${formatEuro(input.netCents)} – ${formatEuro(Math.abs(delta))} ${over ? 'mehr' : 'weniger'}.`,
        })
      }
    }
    if (input.kind === 'deposit_return' && input.reference === null) {
      hint = `${input.description}: Leergut-Gutschrift des Lieferanten über ${formatEuro(Math.abs(input.netCents))}. Die Rechnung bucht das Leergutkonto nicht – ist die Rückgabe dort noch nicht erfasst, bitte als Leergutrückgabe buchen.`
    } else if (input.kind === 'deposit' && input.reference === null) {
      hint = `${input.description}: Pfand ohne Warenzeile – zählt im Pfandabgleich mit.`
    }
  }
  const blocking = differences.filter((entry) => entry.blocking)
  const status = blocking.length > 0 ? 'difference' : differences.length > 0 || (hint !== null && input.kind === 'deposit_return') ? 'info' : 'ok'
  const fallback = input.reference ? `${input.description}: wie laut ${input.reference === 'order' ? 'Bestellung' : 'Wareneingang'}.` : `${input.description}.`
  return {
    status,
    differences,
    blockingCents: blocking.reduce((sum, entry) => sum + Math.max(0, entry.amountCents), 0),
    signature: differenceSignature(blocking),
    message: differences[0]?.message ?? hint ?? fallback,
  }
}
